import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import axiosInstance from "../../utils/axiosUtil";
import LoadingBox from "../layout/LoadingBox";
import MessageBox from "../layout/MessageBox";
import ModernSelect from "../common/ModernSelect";

const vendorOptions = [
  { value: "gemini", label: "Gemini" },
  { value: "claude", label: "Claude" },
  { value: "chatgpt", label: "ChatGPT" },
];

const vendorModelHints = {
  gemini: ["gemini-2.5-pro", "gemini-2.5-flash", "gemini-2.5-flash-lite"],
  claude: ["claude-sonnet-4-20250514", "claude-opus-4-20250514"],
  chatgpt: ["gpt-5", "gpt-4.1", "gpt-4.1-mini"],
};

export default function GenerateReport() {
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("Global Market Size, Share, and Forecast to 2034");
  const [industry, setIndustry] = useState("");
  const [vendor, setVendor] = useState("gemini");
  const [model, setModel] = useState("");
  const [templateKey, setTemplateKey] = useState("market_report_standard");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [reportId, setReportId] = useState("");

  const loadIndustries = async () => {
    setLoadingIndustries(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/industries");
      setIndustries(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        setIndustry(data[0].name);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load industries");
    } finally {
      setLoadingIndustries(false);
    }
  };

  useEffect(() => {
    loadIndustries();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!industry) {
      setStatus("Please select an industry");
      return;
    }

    setSubmitting(true);
    setStatus("Generating report...");
    setReportId("");

    try {
      const { data } = await axiosInstance.post("/reports", {
        title,
        industry,
        vendor,
        model: model.trim(),
        templateKey,
      });
      setStatus("Report generated successfully");
      if (data?._id) {
        setReportId(data._id);
      }
    } catch (err) {
      setStatus(err?.response?.data?.message || "Failed to generate report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="page-section py-4">
      <Row className="mb-4">
        <Col md={12}>
          <h3 className="page-title">Generate Market Report</h3>
          <p className="page-subtitle mb-0">
            Provide a title, select an industry, and choose the vendor. If you leave the model empty, the vendor default from `.env` is used for the full report run.
          </p>
        </Col>
      </Row>

      <Row>
        <Col lg={7} md={8}>
          <Card>
            <Card.Body>
              <Card.Title>Report details</Card.Title>
              <div className="card-divider" />
              {loadingIndustries ? (
                <div className="py-4 text-center">
                  <LoadingBox />
                </div>
              ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="vendor">
                    <Form.Label>Vendor</Form.Label>
                    <ModernSelect
                      value={vendor}
                      onChange={(event) => {
                        const nextVendor = event.target.value;
                        setVendor(nextVendor);
                        if (
                          model &&
                          !vendorModelHints[nextVendor]?.includes(model.trim())
                        ) {
                          setModel("");
                        }
                      }}
                      placeholder="Select vendor"
                    >
                      {vendorOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </ModernSelect>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="model">
                    <Form.Label>Model</Form.Label>
                    <Form.Control
                      type="text"
                      value={model}
                      onChange={(event) => setModel(event.target.value)}
                      placeholder={`Optional. Leave blank for the ${vendor} default model.`}
                    />
                    <Form.Text className="text-muted">
                      One vendor/model pair is reused for the entire report generation run.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="template">
                    <Form.Label>Template</Form.Label>
                    <ModernSelect
                      value={templateKey}
                      onChange={(event) => setTemplateKey(event.target.value)}
                      placeholder="Select template"
                    >
                      <option value="market_report_standard">Template 1 — Standard Market Report</option>
                      <option value="market_report_template2">Template 2 — Market Report</option>
                      <option value="market_report_template3">Template 3 — Market Report</option>
                      <option value="market_report_template4">Template 4 — Market Report</option>
                      <option value="market_report_template5">Template 5 — Market Report</option>
                      <option value="market_report_template6">Template 6 — Market Report</option>
                      <option value="market_report_template7">Template 7 — Market Report</option>
                      <option value="market_report_template8">Template 8 — Market Report</option>
                    </ModernSelect>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="title">
                    <Form.Label>Report title</Form.Label>
                    <Form.Control
                      type="text"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="Market report title"
                      required
                    />
                  </Form.Group>

                  <Button type="submit" disabled={submitting || industries.length === 0}>
                    {submitting ? "Generating..." : "Generate report"}
                  </Button>
                </Form>
              )}

              {status && (
                <Card className="mt-4">
                  <Card.Body>
                    <p className="mb-1">{status}</p>
                    {reportId && (
                      <small className="text-muted">Report ID: {reportId}</small>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
